import { Component, OnInit, OnDestroy, HostListener, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CatalogueApiService } from '@angular-workspace/services';
import { HelperService } from '../../../shared/services/helper.service';
import { GoogleAnalyticsService } from '../../../services/google-analytics.service';
import { AssetTypeMapperService } from '@angular-workspace/core';
import { catalogueDetailAuditQueryParams } from '../../../shared/utils/catalogue-asset-detail-navigation.util';

export interface SearchResult {
  id: string;
  name: string;
  label: string;
  type: string[];
  displayType: string;
}

export interface GroupedResults {
  datasets: SearchResult[];
  models: SearchResult[];
  usecases: SearchResult[];
}

@Component({
  selector: 'app-banner',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './banner.component.html',
  styleUrl: './banner.component.scss',
})
export class BannerComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('bannerVideo') bannerVideo!: ElementRef<HTMLVideoElement>;

  searchQuery = '';
  searchResults: SearchResult[] = [];
  groupedResults: GroupedResults = {
    datasets: [],
    models: [],
    usecases: []
  };
  showResults = false;
  isLoading = false;

  get visibleResultsCount(): number {
    return this.groupedResults.datasets.length + this.groupedResults.models.length;
  }

  // Default asset types to search
  private readonly defaultAssetTypes = ['Dataset', 'Model', 'App'];

  // Default filter fields to return from API
  private readonly defaultFilterFields = ['id', 'name', 'type', 'label'];

  // Minimum characters required to trigger auto-search
  private readonly MIN_SEARCH_LENGTH = 3;

  // Debounced search function
  private debouncedSearch: ((query: string) => void) | null = null;

  constructor(
    private catalogueApiService: CatalogueApiService,
    private helperService: HelperService,
    private googleAnalytics: GoogleAnalyticsService,
    private assetTypeMapper: AssetTypeMapperService,
    private router: Router
  ) { }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const searchContainer = target.closest('.hero--search-container');
    if (!searchContainer) {
      this.showResults = false;
      this.searchQuery = '';
    }
  }

  ngOnInit(): void {
    // Create debounced search function (500ms delay)
    this.debouncedSearch = this.helperService.debounce(
      (query: string) => this.performSearch(query),
      500
    );
  }

  ngAfterViewInit(): void {
    // Ensure video plays after view initializes
    this.ensureVideoPlays();
  }

  ngOnDestroy(): void {
    this.debouncedSearch = null;
  }

  /**
   * Ensure the banner video plays properly
   * This handles browser autoplay restrictions
   */
  private ensureVideoPlays(): void {
    if (this.bannerVideo?.nativeElement) {
      const video = this.bannerVideo.nativeElement;

      // Ensure video is muted (required for autoplay)
      video.muted = true;
      video.playsInline = true;

      // Add event listeners
      video.addEventListener('loadedmetadata', () => {
        console.log('Video metadata loaded');
      });

      video.addEventListener('canplay', () => {
        console.log('Video can play');
        // Try to play when video is ready
        this.attemptPlay(video);
      });

      // Add a small delay to ensure DOM is ready
      setTimeout(() => {
        // Load the video first
        video.load();

        // Try to play immediately
        this.attemptPlay(video);
      }, 200);
    }
  }

  /**
   * Attempt to play the video with error handling
   */
  private attemptPlay(video: HTMLVideoElement): void {
    const playPromise = video.play();

    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          // Video is playing successfully
          console.log('Banner video is playing');
        })
        .catch((error) => {
          // Autoplay was prevented
          console.warn('Banner video autoplay was prevented:', error);

          // Try to play on user interaction
          const playOnInteraction = () => {
            video.muted = true; // Ensure it's still muted
            video.play()
              .then(() => console.log('Video playing after user interaction'))
              .catch(err => console.error('Failed to play video:', err));
          };

          // Listen for any user interaction
          document.addEventListener('click', playOnInteraction, { once: true });
          document.addEventListener('touchstart', playOnInteraction, { once: true });
          document.addEventListener('keydown', playOnInteraction, { once: true });
        });
    }
  }

  /**
   * Handle input event - triggers search when user types 3+ characters
   */
  onSearchInput(): void {
    const trimmedQuery = this.searchQuery?.trim() || '';

    // Clear results if query is too short
    if (trimmedQuery.length < this.MIN_SEARCH_LENGTH) {
      this.searchResults = [];
      this.groupedResults = {
        datasets: [],
        models: [],
        usecases: []
      };
      this.showResults = false;
      return;
    }

    // Only trigger search if user has typed at least 3 characters
    if (trimmedQuery.length >= this.MIN_SEARCH_LENGTH && this.debouncedSearch) {
      this.debouncedSearch(trimmedQuery);
    }
  }

  /**
   * Handle search button click or Enter key press
   */
  onSearch(): void {
    const trimmedQuery = this.searchQuery?.trim() || '';

    if (!trimmedQuery) {
      console.warn('Search query is empty');
      this.showResults = false;
      return;
    }

    // If there are results, navigate to the first one
    // Otherwise, perform search and navigate to search results page
    if (this.searchResults.length > 0) {
      this.navigateToAsset(this.searchResults[0]);
    } else {
      this.performSearch(trimmedQuery, true);
    }
  }

  /**
   * Handle input focus - show results if available
   */
  onInputFocus(): void {
    if (this.searchResults.length > 0 && this.searchQuery.trim().length >= this.MIN_SEARCH_LENGTH) {
      this.showResults = true;
    }
  }

  /**
   * Perform the actual search API call
   * @param query The search query string
   * @param navigateToSearchPage Whether to navigate to search results page after search
   */
  private async performSearch(query: string, navigateToSearchPage: boolean = false): Promise<void> {
    try {
      this.isLoading = true;
      const response = await this.catalogueApiService.searchAllAssets(
        query,
        this.defaultAssetTypes,
        this.defaultFilterFields,
        1, // page
        1000 // size - set to 1000 for banner component search
      );

      // Parse response - handle different response structures
      let results: any[] = [];
      if (Array.isArray(response)) {
        results = response;
      } else if (response?.result && Array.isArray(response.result)) {
        results = response.result;
      } else if (response?.data && Array.isArray(response.data)) {
        results = response.data;
      } else if (response?.items && Array.isArray(response.items)) {
        results = response.items;
      }

      // Transform API results to SearchResult format
      this.searchResults = results.map((item: any) => {
        // Get first type from array or use empty string
        const apiType = Array.isArray(item.type) ? item.type[0] : item.type || '';
        const displayType = this.assetTypeMapper.mapApiFormatToAssetType(apiType);

        return {
          id: item.id || '',
          name: item.name || '',
          label: item.label || item.name || '',
          type: Array.isArray(item.type) ? item.type : [item.type || ''],
          displayType: displayType
        };
      }).filter((result: SearchResult) => result.id); // Filter out items without ID

      // Group results by type
      this.groupedResults = {
        datasets: this.searchResults.filter(r => r.displayType === 'Dataset'),
        models: this.searchResults.filter(r => r.displayType === 'Model'),
        usecases: this.searchResults.filter(r => r.displayType === 'Use Case' || r.displayType === 'App')
      };

      // Show results dropdown for both results and no-results (so no-results message is visible)
      this.showResults = !navigateToSearchPage;

      // Navigate to search results page if requested
      if (navigateToSearchPage) {
        // TODO: Navigate to search results page
        // this.router.navigate(['/catalogue/search'], { queryParams: { q: query } });
      }
    } catch (error) {
      console.error('Error performing search:', error);
      this.searchResults = [];
      this.groupedResults = {
        datasets: [],
        models: [],
        usecases: []
      };
      this.showResults = false;
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Navigate to asset detail page
   * @param result The search result to navigate to
   */
  navigateToAsset(result: SearchResult): void {
    this.showResults = false;
    this.searchQuery = ''; // Clear search input when navigating

    // Determine route based on asset type
    const apiType = result.type?.[0] || '';
    let route = '/catalogue';

    if (apiType.includes('DataBank')) {
      this.googleAnalytics.trackDatasetClick(result.name || result.label || '', result.id);
      route = `/dataset/${result.id}`;
    } else if (apiType.includes('AiModel')) {
      this.googleAnalytics.trackModelClick(result.name || result.label || '', result.id);
      route = `/model/${result.id}`;
    } else if (apiType.includes('Apps')) {
      route = `/app/${result.id}`;
    } else if (apiType.includes('UseCase')) {
      route = `/usecase/${result.id}`;
    }

    this.router.navigate([route], { queryParams: catalogueDetailAuditQueryParams() });
  }

  /**
   * Navigate to view all results for a specific asset type
   * @param section The section type ('datasets', 'models', 'usecases')
   */
  viewAllResults(section: 'datasets' | 'models' | 'usecases'): void {
    this.showResults = false;

    // Navigate to the respective listing page with search query
    let route = '/catalogue';

    switch (section) {
      case 'datasets':
        route = '/datasets';
        break;
      case 'models':
        route = '/models';
        break;
      case 'usecases':
        route = '/usecases';
        break;
    }

    // Navigate with search query parameter
    const searchQuery = this.searchQuery.trim();
    this.searchQuery = ''; // Clear search input after navigation

    this.router.navigate([route], {
      queryParams: { search: searchQuery }
    });
  }

  /**
   * Close search results dropdown
   */
  closeResults(): void {
    this.showResults = false;
    this.searchQuery = '';
  }

  scrollToStatistics(): void {
    const statisticsElement = document.getElementById('statistics-section');
    if (statisticsElement) {
      statisticsElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  }
}


